import {useIssueState} from '@github-ui/issue-pr-state'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {ActionList} from '@primer/react'
import type React from 'react'
import {graphql, useFragment} from 'react-relay'

import {getIssueTitle} from '../../../utils/helpers'
import {useParentIssueSubscription} from '../../ParentIssueSubscription'
import type {ParentIssueFragment$key} from './__generated__/ParentIssueFragment.graphql'
import {SubIssuesSummary} from './SubIssuesSummary'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const ParentIssueFragment = graphql`
  fragment ParentIssueFragment on Issue {
    id
    title
    titleHTML
    url
    number
    repository {
      nameWithOwner
    }
    state
    stateReason
    ...SubIssuesSummary
  }
`

type ParentIssueProps = {
  onLinkClick?: (event: MouseEvent) => void
}

type ParentIssueInternalProps = Omit<ParentIssueProps, 'queryRef'> & {
  issueKey: ParentIssueFragment$key
}

export function ParentIssue({issueKey, onLinkClick}: ParentIssueInternalProps) {
  const data = useFragment(ParentIssueFragment, issueKey)

  useParentIssueSubscription(data.id)
  const {sourceIcon} = useIssueState({
    state: data.state,
    stateReason: data.stateReason,
  })

  const IconComponent = sourceIcon('Issue')
  /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side */
  const title = getIssueTitle(data.title, data.titleHTML) as SafeHTMLString

  return (
    <ActionList.LinkItem
      href={data.url}
      target="_blank"
      onClick={
        onLinkClick
          ? (event: React.MouseEvent<HTMLElement>) => {
              onLinkClick(event.nativeEvent)
            }
          : undefined
      }
    >
      <ActionList.LeadingVisual>
        {/* eslint-disable-next-line react-hooks/static-components */}
        <IconComponent />
      </ActionList.LeadingVisual>
      <ActionList.TrailingVisual>
        <SubIssuesSummary issue={data} />
      </ActionList.TrailingVisual>
      <VerifiedHTMLText html={title} className="markdown-title" />
      <ActionList.Description variant="block">
        {data.repository.nameWithOwner}#{data.number}
      </ActionList.Description>
    </ActionList.LinkItem>
  )
}
